import { useState } from "react";
import { Link } from "react-router-dom";
import { BarChart2, ArrowLeft, CheckCircle, Mail } from "lucide-react";
import api from "../../utils/api";
import { toast } from "sonner";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex overflow-hidden">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-5/12 relative flex-col justify-between p-12" style={{ backgroundColor: "#282560" }}>
        <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 30% 50%, #1B75BA 0%, transparent 60%)" }} />
        <div className="relative z-10" />
        <div className="relative z-10 flex flex-col items-center text-center">
          <img src="/images/logo-white.png" alt="OnPoint BI" className="w-72 h-auto mb-10" data-testid="forgot-hero-logo" />
          <h2 className="text-3xl font-bold text-white mb-3 leading-tight" style={{ fontFamily: "Inter, sans-serif" }}>
            Reset your password
          </h2>
          <p className="text-white/50 text-sm leading-relaxed max-w-xs">
            Provide the email address associated with your account to recover your password.
          </p>
        </div>
        <div className="relative z-10">
          <p className="text-white/25 text-xs">© 2024 OnPoint Business Intelligence, LLC</p>
        </div>
      </div>

      {/* Right Panel */}
      <div className="w-full lg:w-7/12 flex items-center justify-center p-6 lg:p-14 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img src="/images/logo-colored.png" alt="OnPoint BI" className="w-56 h-auto" data-testid="forgot-mobile-logo" />
          </div>

          {submitted ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: "#F0FFF4", border: "1.5px solid #FED7AA" }}>
                <CheckCircle className="w-8 h-8" style={{ color: "#D4A017" }} />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ color: "#1C2040", fontFamily: "Inter, sans-serif" }}>Check your inbox</h2>
              <p className="text-sm mb-6 leading-relaxed" style={{ color: "#6B7491" }}>
                If <span style={{ color: "#1C2040", fontWeight: 600 }}>{email}</span> is registered, you'll receive reset instructions shortly.
              </p>
              <Link to="/login" data-testid="back-to-login-link"
                className="inline-flex items-center gap-2 text-sm font-medium hover:underline" style={{ color: "#1B75BA" }}>
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-1.5" style={{ color: "#1C2040", fontFamily: "Inter, sans-serif" }}>Reset your password</h2>
                <p className="text-sm" style={{ color: "#6B7491" }}>Enter your email to receive a password reset link.</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: "#1C2040" }}>Email address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#6B7491" }} />
                    <input data-testid="forgot-email-input" type="email" value={email}
                      onChange={(e) => setEmail(e.target.value)} placeholder="Your Email Address" required
                      className="w-full h-11 pl-10 pr-4 rounded-lg text-sm outline-none"
                      style={{ backgroundColor: "#F7F8FA", border: "1.5px solid #D9D9D9", color: "#1C2040" }}
                      onFocus={(e) => e.target.style.borderColor = "#1B75BA"}
                      onBlur={(e) => e.target.style.borderColor = "#D9D9D9"}
                    />
                  </div>
                </div>
                <button data-testid="forgot-submit-btn" type="submit" disabled={loading}
                  className="w-full h-11 rounded-lg font-semibold text-sm text-white disabled:opacity-60"
                  style={{ backgroundColor: "#1B75BA" }}>
                  {loading ? "Sending..." : "Send Reset Link"}
                </button>
              </form>
              <div className="mt-5 text-center">
                <Link to="/login" data-testid="back-to-login-link"
                  className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline" style={{ color: "#6B7491" }}>
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
